"use client";

import { createContext, useContext, type ReactNode } from "react";

import MarketingLink from "@/components/common/LinkHandler/MarketingLink";
import Tag from "@/components/common/Tag/Tag";
import Picture from "@/components/ui-kit/Picture/Picture";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { cn } from "@/utils/general";

type Variant = "stacked" | "inline";

type ResultsCardContextValue = {
  variant: Variant;
};

const ResultsCardContext = createContext<ResultsCardContextValue | null>(null);

function useResultsCardContext() {
  const ctx = useContext(ResultsCardContext);
  if (!ctx) {
    throw new Error("ResultsCard.* components must be used inside <ResultsCard>");
  }
  return ctx;
}

type ResultsCardRootProps = {
  variant?: Variant;
  children: ReactNode;
  className?: string;
};

export function Root({ variant = "stacked", children, className }: ResultsCardRootProps) {
  return (
    <ResultsCardContext.Provider value={{ variant }}>
      <article
        className={cn(
          "flex w-full h-full",
          variant === "stacked" && "flex-col",
          variant === "inline" && "flex-col xl:flex-row",
          className
        )}
      >
        {children}
      </article>
    </ResultsCardContext.Provider>
  );
}

function ResultsCardImageGroup({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const { variant } = useResultsCardContext();

  return (
    <div
      className={cn(
        "flex justify-around overflow-hidden",
        variant === "stacked" && "w-full rounded-t flex-shrink-0",
        variant === "inline" && "w-full xl:w-2/3 rounded-t xl:rounded-tr-none xl:rounded-l",
        className
      )}
    >
      {children}
    </div>
  );
}

type ResultsCardImageProps = {
  src: string;
  alt?: string;
  tag?: string;
  imageDirection?: "left" | "right" | "center";
  badgePosition?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  className?: string;
};

/* Full class names so Tailwind can detect and generate them (no dynamic `object-${dir}`). */
const objectPositionClasses = {
  left: "object-left",
  right: "object-right",
  center: "object-center",
} as const;

function ResultsCardImage({
  src,
  alt,
  tag,
  badgePosition = "bottom-left",
  imageDirection = "center",
  className,
}: ResultsCardImageProps) {
  const srcStr: string = typeof src === "string" ? src : "";
  return (
    <div className="relative w-full h-full min-h-0">
      {tag && (
        <div
          className={cn(
            "absolute",
            badgePosition === "top-left" && "top-2 left-2",
            badgePosition === "top-right" && "top-2 right-2",
            badgePosition === "bottom-left" &&
              "bottom-1 left-1 md:bottom-2 md:left-2 lg:bottom-3 lg:left-3",
            badgePosition === "bottom-right" && "bottom-2 right-2"
          )}
        >
          <Tag
            data={{
              line: tag,
              classes: "bg-nova-base-solid-20",
            }}
          />
        </div>
      )}
      <Picture
        src={srcStr}
        alt={alt ?? ""}
        className={cn(
          "w-full h-full object-cover",
          objectPositionClasses[imageDirection] ?? objectPositionClasses.center,
          className
        )}
      />
    </div>
  );
}

function ResultsCardContent({ children, className }: { children: ReactNode; className?: string }) {
  const { variant } = useResultsCardContext();

  return (
    <div
      className={cn(
        "flex flex-col flex-1 py-4 md:py-6 lg:py-8 px-4 md:px-6 lg:px-8",
        variant === "stacked" &&
          "w-full border-l border-r border-b rounded-b border-nova-transparency-black-20",
        variant === "inline" &&
          `
            w-full xl:w-1/3 
            border-l border-r border-b xl:border-l-0 xl:border-t border-nova-transparency-black-20
            rounded-b xl:rounded-bl-none xl:rounded-r
          `,
        className
      )}
    >
      {children}
    </div>
  );
}

type TagsProps = {
  variant?: "masterbrand" | "men";
  tags?: string[];
  className?: string;
};

function ResultsCardTags({ variant = "masterbrand", tags = [], className }: TagsProps) {
  if (!tags.length) return null;

  if (variant === "men") {
    return (
      <div className={cn("flex flex-wrap gap-1", className)}>
        {tags?.map((tag) => (
          <Tag
            key={tag}
            data={{
              line: tag,
              classes: "bg-nova-brand-darkblue-60",
              fontclasses: "text-nova-base-white",
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {tags?.map((tag) => (
        <Tag
          key={tag}
          data={{
            line: tag,
            classes: "bg-nova-base-solid-20",
          }}
        />
      ))}
    </div>
  );
}

type ProductProps = {
  variant?: "masterbrand" | "men";
  product?: string;
  action?: string;
  pdp?: string;
  className?: string;
};

function ResultsCardProduct({
  variant = "masterbrand",
  product,
  action,
  pdp,
  className,
}: ProductProps) {
  if (!product && !action) return null;

  return (
    <div className={cn("inline", className)}>
      <TextElementNova
        element="span"
        className="CTA-20-Dia-Normal text-nova-transparency-black-70"
        text={action}
      />
      {pdp ? (
        <MarketingLink path={pdp} module_location="mid_fold" module_name="Before & After">
          <TextElementNova
            element="span"
            className={cn(
              "CTA-20-Dia-Normal ml-1 underline text-nova-brand-primary-80",
              variant === "men" && "CTA-10-Dia-Mono"
            )}
            text={product}
          />
        </MarketingLink>
      ) : (
        <TextElementNova
          element="span"
          className={cn(
            "CTA-20-Dia-Normal text-nova-brand-primary-80 ml-1",
            variant === "men" && "text-nova-brand-darkblue-60"
          )}
          text={product}
        />
      )}
    </div>
  );
}
Root.ImageGroup = ResultsCardImageGroup;
Root.Image = ResultsCardImage;
Root.Content = ResultsCardContent;
Root.Tags = ResultsCardTags;
Root.Product = ResultsCardProduct;

export const ResultsCard = Object.assign(Root, {
  Root,
  ImageGroup: ResultsCardImageGroup,
  Image: ResultsCardImage,
  Content: ResultsCardContent,
  Tags: ResultsCardTags,
  Product: ResultsCardProduct,
});
